import React from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  overflow: hidden;
  background: #fff; /* match site background */
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);

  @media (max-width: 768px) {
    background: transparent; /* let the page background show, no thick frame */
    border-radius: 0;
    box-shadow: none;
    padding: 8px 0 12px; /* give the image a little breathing room */
  }
`;

const Track = styled.div`
  display: flex;
  transition: transform 300ms ease;
  will-change: transform;
`;

const Slide = styled.div`
  flex: 0 0 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  background: transparent;

  @media (max-width: 768px) {
    min-height: auto;
    align-items: flex-start; /* anchor image to the top to avoid tall top border */
  }
`;

const Img = styled.img`
  display: block;
  width: auto;
  height: 80vh;
  max-height: 80vh;
  max-width: 100%;
  object-fit: contain;
  background: transparent;
  border-radius: 0;

  @media (max-width: 1024px) {
    height: 70vh;
    max-height: 70vh;
  }
  @media (max-width: 768px) {
    width: 100%;
    height: auto;     /* scale by width to avoid letterboxing */
    max-height: none; /* allow natural height */
  }
`;

const Controls = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none; /* allow clicks to fall through except on buttons */
  
  @media (max-width: 768px) {
    display: none; /* hide overlay controls on mobile */
  }
`;

const ArrowButton = styled.button`
  pointer-events: all;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid #000;
  background: #000; /* black button */
  color: #fff;   /* parchment text */
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0,0,0,0.18);

  &:hover { background: #111; }
  &:focus { outline: 2px solid #fff; outline-offset: 2px; }

  ${props => props.left ? 'left: 12px;' : 'right: 12px;'}
  
  @media (max-width: 768px) {
    display: none; /* don't cover artwork on mobile */
  }
`;

const Footer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  pointer-events: all;
`;

const Counter = styled.span`
  font-weight: 700;
  background: #000; /* match buttons */
  color: #fff;   /* parchment text */
  border: 1px solid #000;
  padding: 6px 10px;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
`;

/* Mobile-only bottom control bar to avoid covering artwork */
const MobileBar = styled.div`
  display: none;
  margin-top: 14px; /* more space below image */
  width: 100%;
  display: none;
  align-items: center;
  justify-content: center;
  gap: 10px;

  @media (max-width: 768px) {
    display: flex; /* show only on mobile */
  }
`;

const MobileButton = styled.button`
  border: 1px solid #000;
  background: #000;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 700;
`;

const MobileCounter = styled(Counter)`
  position: static;
`;

export default function Slideshow({ images = [], startIndex = 0, ariaLabelPrefix = 'Slide' }) {
  const [index, setIndex] = React.useState(startIndex);
  const trackRef = React.useRef(null);
  const touch = React.useRef({ startX: 0, deltaX: 0, active: false });

  const prev = React.useCallback(() => {
    setIndex(i => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const next = React.useCallback(() => {
    setIndex(i => (i + 1) % images.length);
  }, [images.length]);

  // Keyboard navigation when wrapper focused
  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
  };

  // Swipe support
  const onTouchStart = (e) => {
    const x = e.touches[0].clientX;
    touch.current = { startX: x, deltaX: 0, active: true };
  };
  const onTouchMove = (e) => {
    if (!touch.current.active) return;
    touch.current.deltaX = e.touches[0].clientX - touch.current.startX;
  };
  const onTouchEnd = () => {
    if (!touch.current.active) return;
    const threshold = 50;
    if (touch.current.deltaX > threshold) prev();
    else if (touch.current.deltaX < -threshold) next();
    touch.current.active = false;
    touch.current.deltaX = 0;
  };

  // Apply transform
  const offset = -index * 100;

  return (
    <>
    <Wrapper
      role="region"
      aria-label="Image slideshow"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <Track ref={trackRef} style={{ transform: `translateX(${offset}%)` }}>
        {images.map((src, i) => (
          <Slide key={src + i} aria-label={`${ariaLabelPrefix} ${i + 1}`}>
            <Img src={src} alt={`${ariaLabelPrefix} ${i + 1}`} />
          </Slide>
        ))}
      </Track>

      {images.length > 1 && (
        <Controls aria-hidden>
          <ArrowButton left onClick={prev} aria-label="Previous slide">Prev</ArrowButton>
          <ArrowButton onClick={next} aria-label="Next slide">Next</ArrowButton>
          <Footer>
            <Counter>{index + 1} / {images.length}</Counter>
          </Footer>
        </Controls>
      )}
    </Wrapper>

    {images.length > 1 && (
      <MobileBar>
        <MobileButton onClick={prev} aria-label="Previous slide">Prev</MobileButton>
        <MobileCounter>{index + 1} / {images.length}</MobileCounter>
        <MobileButton onClick={next} aria-label="Next slide">Next</MobileButton>
      </MobileBar>
    )}
    </>
  );
}
